import { ENVIRONMENT } from '../../../../environments/environment';
import type { CommerceLanding } from '../../../domain/commerce-registry/models/commerce-landing.model';
import type { CommerceRegistryThemePalette } from '../../../domain/commerce-registry/models/commerce-registry-theme-palette.model';
import { commerceRegistryThemePaletteMock } from './commerce-registry-theme-palette.mock';
import {
  COMMERCE_LANDING_MOCK_DEV_OVERRIDES,
  createCommerceLandingMock,
} from './commerce-landing.mock';
import { defaultCommerceRegistryThemePalette } from '../theme/default-commerce-registry-theme-palette';

function mergePaletteFields(
  partial: Partial<CommerceRegistryThemePalette> | undefined,
  defaults: CommerceRegistryThemePalette,
): CommerceRegistryThemePalette {
  return {
    primary: partial?.primary?.trim() || defaults.primary,
    secondary: partial?.secondary?.trim() || defaults.secondary,
    tertiary: partial?.tertiary?.trim() || defaults.tertiary,
    textHint: partial?.textHint?.trim() || defaults.textHint,
    input: partial?.input?.trim() || defaults.input,
    typography: partial?.typography?.trim() || defaults.typography,
    backgroundImage: partial?.backgroundImage?.trim() || defaults.backgroundImage,
  };
}

/**
 * After a successful Firebase read:
 * - `mockEnabled`: fill missing theme fields from the dev palette mock (orange / Figma sample).
 * - otherwise: fill missing fields from the Leal default skeleton palette.
 *
 * When `mockEnabled`, replaces `logomarca` with the URL from `createCommerceLandingMock` (dev mock).
 * Does not alter banners, países, términos, etc. otherwise.
 */
export function applyCommerceThemeDefaults(landing: CommerceLanding): CommerceLanding {
  const defaults = ENVIRONMENT.mockEnabled
    ? commerceRegistryThemePaletteMock
    : defaultCommerceRegistryThemePalette;

  if (ENVIRONMENT.preferSkeletonCommerceTheme && !ENVIRONMENT.mockEnabled) {
    return {
      ...landing,
      commerce_theme: mergePaletteFields(undefined, defaultCommerceRegistryThemePalette),
    };
  }

  const themeMerged: CommerceLanding = {
    ...landing,
    commerce_theme: mergePaletteFields(landing.commerce_theme, defaults),
  };
  if (ENVIRONMENT.mockEnabled) {
    return {
      ...themeMerged,
      logomarca: createCommerceLandingMock(landing.comercio_slug).logomarca,
    };
  }
  return themeMerged;
}

/**
 * Keys read from {@link createCommerceLandingMock} (template in `commerce-landing.mock.ts`) and
 * layered on the RTDB landing when `mockEnabled`. RTDB still owns programName, banners, países, etc.
 */
const MOCK_TEMPLATE_OVERLAY_KEYS: (keyof CommerceLanding)[] = [
  'withe_label',
  'otp_habilitado',
  'ms_consent_habilitado',
  'referenciaOrigen',
  'home_view',
  'home_actions',
  'data_protection',
  'privacy_policy',
  'terms_conditions',
];

/**
 * When `mockEnabled`, merges the mock template + {@link COMMERCE_LANDING_MOCK_DEV_OVERRIDES} on top
 * of the landing from Firebase (after {@link applyCommerceThemeDefaults}). `commerce_theme` fill
 * stays in {@link applyCommerceThemeDefaults} so RTDB colors are not replaced by the full demo palette.
 */
export function applyMockDevFieldOverrides(landing: CommerceLanding): CommerceLanding {
  if (!ENVIRONMENT.mockEnabled) return landing;

  const mock = createCommerceLandingMock(landing.comercio_slug);
  const o = COMMERCE_LANDING_MOCK_DEV_OVERRIDES;

  let merged: CommerceLanding = { ...landing };

  const row = merged as unknown as Record<string, unknown>;
  for (const key of MOCK_TEMPLATE_OVERLAY_KEYS) {
    const v = mock[key];
    if (v !== undefined) {
      row[key as string] = v;
    }
  }

  const { commerce_theme: themeFromOverrides, ...restOverrides } = o;
  merged = { ...merged, ...restOverrides };

  if (themeFromOverrides) {
    merged.commerce_theme = {
      ...(merged.commerce_theme ?? (themeFromOverrides as CommerceRegistryThemePalette)),
      ...themeFromOverrides,
    };
  }

  return merged;
}
